import { defineComponent as _defineComponent } from 'vue'
import { watch, ref, computed, onMounted } from 'vue'
import { useApolloClient } from '@vue/apollo-composable'
import {
  useUpdateImpactMutation,
  useUpdateImpactGoalMutation,
  useGetImpactGoalQuery,
} from '~/types/types'
import type { ImpactGoalInput } from '~/types/types'
import { GHG_ESTIMATOR_CONCEPT_ID } from '~/constants/concepts'


export default /*#__PURE__*/_defineComponent({
  __name: 'StartingYearWithWarning',
  props: {
    ventureId: null,
    disabled: { type: Boolean, default: false },
    controlled: { type: Boolean, default: false },
    startingYearFromParent: { default: undefined }
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: number): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { ventureId: string, disabled: boolean, controlled: boolean, startingYearFromParent: number | null };





const { result } = useGetImpactGoalQuery(
  () => ({ ventureId: props.ventureId }),
  () => ({
    skip: props.controlled,
  }),
)
const impactGoal = computed(() => result?.value?.getImpactGoal ?? {})
const ghgImpact = computed(() =>
  (impactGoal.value?.impacts ?? []).find(
    (impact: any) => Number(impact?.conceptId) === GHG_ESTIMATOR_CONCEPT_ID,
  ),
)

const { mutate: updateImpactGoal, onDone } = useUpdateImpactGoalMutation()
const { mutate: updateImpact, onDone: onUpdateImpactDone } = useUpdateImpactMutation()
const { client: apolloClient } = useApolloClient()
const currentYear = new Date().getFullYear()

const resetYearBaseline = () => {
  if (props.controlled) {
    const y = Number(props.startingYearFromParent)
    return Number.isFinite(y) ? y : currentYear
  }
  const raw = Number.parseInt(
    String(ghgImpact.value?.startingYear ?? impactGoal.value.ghgStartingYear ?? currentYear),
    10,
  )
  return Number.isNaN(raw) ? currentYear : raw
}

const dialogForStartingYear = ref(false)
const hasMounted = ref(false)
const selectedStartingYear = ref(resetYearBaseline())

watch(
  () => props.startingYearFromParent,
  (y) => {
    if (!props.controlled) return
    const parsed = Number(y)
    if (Number.isFinite(parsed)) {
      selectedStartingYear.value = parsed
    }
  },
)

const yearOptions = computed(() =>
  Array.from({ length: 10 }, (_, index) => 2020 + index),
)

onMounted(() => {
  hasMounted.value = true
})

const triggerUpdateImpactGoal = (
  key: keyof Omit<ImpactGoalInput, 'id'>,
  val: number,
) => {
  const input = { id: impactGoal.value.id, [key]: val } as ImpactGoalInput
  updateImpactGoal({ input })
}

const triggerUpdateStartingYear = (val: number) => {
  if (ghgImpact.value?.id) {
    updateImpact({
      id: ghgImpact.value.id,
      input: {
        startingYear: val,
      },
    })
    return
  }
  triggerUpdateImpactGoal('ghgStartingYear', val)
}

const confirmStartingYear = () => {
  if (props.controlled) {
    emit('input', selectedStartingYear.value)
    return
  }
  triggerUpdateStartingYear(selectedStartingYear.value)
}

onDone(() => {
  apolloClient.cache.evict({ fieldName: 'listMetricAssignments' })
  apolloClient.cache.gc()
})

onUpdateImpactDone(() => {
  apolloClient.cache.evict({ fieldName: 'listMetricAssignments' })
  apolloClient.cache.gc()
})

const onSelectStartingYear = (val: any) => {
  if (props.disabled) return
  if (!hasMounted.value) return
  const parsed = Number.parseInt(String(val), 10)
  if (Number.isNaN(parsed)) {
    selectedStartingYear.value = currentYear
    return
  }
  if (parsed === selectedStartingYear.value) return
  selectedStartingYear.value = parsed
  dialogForStartingYear.value = true
}

return { __sfc: true,props, emit, result, impactGoal, ghgImpact, updateImpactGoal, onDone, updateImpact, onUpdateImpactDone, apolloClient, currentYear, resetYearBaseline, dialogForStartingYear, hasMounted, selectedStartingYear, yearOptions, triggerUpdateImpactGoal, triggerUpdateStartingYear, confirmStartingYear, onSelectStartingYear }
}

})